import React, { useState, useCallback, memo } from 'react';
import { CodeBlockRenderer } from './CodeBlockRenderer';
import { SvgRenderer } from './SvgRenderer';
import '../../styles/svgCodeBlockRenderer.css';

export interface SvgCodeBlockRendererProps {
  /** SVG 源代码 */
  readonly code: string;
  /** 代码语言标识 */
  readonly language: string;
}

type ViewMode = 'code' | 'preview';

/**
 * SVG 代码块专用渲染器
 *
 * 功能：
 * - 默认显示代码模式（语法高亮）
 * - 右上角提供「预览 / 代码」切换按钮
 * - 预览模式下渲染 SVG 图片
 */
export const SvgCodeBlockRenderer: React.FC<SvgCodeBlockRendererProps> = memo(({ code, language }) => {
  // 默认 preview
  const [viewMode, setViewMode] = useState<ViewMode>('preview');

  const toggleMode = useCallback((): void => {
    setViewMode(prev => (prev === 'code' ? 'preview' : 'code'));
  }, []);

  return (
    <div className="svg-code-block-wrapper">
      {/* 切换按钮浮层 */}
      <button
        type="button"
        className="svg-toggle-btn"
        onClick={toggleMode}
        title={viewMode === 'code' ? '切换到 SVG 预览' : '切换到源代码'}
      >
        {viewMode === 'code' ? (
          <>
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
              <circle cx="12" cy="12" r="3" />
            </svg>
            <span>预览</span>
          </>
        ) : (
          <>
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="16 18 22 12 16 6" />
              <polyline points="8 6 2 12 8 18" />
            </svg>
            <span>代码</span>
          </>
        )}
      </button>

      {/* 内容区域 */}
      {viewMode === 'code' ? (
        <CodeBlockRenderer code={code} language={language} />
      ) : (
        <div className="svg-preview-area">
          <SvgRenderer content={code} maxWidth={800} maxHeight={600} />
        </div>
      )}
    </div>
  );
});

SvgCodeBlockRenderer.displayName = 'SvgCodeBlockRenderer';